import { z } from "zod";
import { importSourceSchema, membershipStatusSchema, placementStatusSchema } from "../enums.ts";
import { isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * The Families page (onboarding version 2, 2026-10-04). The owner lists families at their own
 * pace: paste a list, a CSV or one family (a WhatsApp group comes in through the sync tool); put
 * each child in a group; invite
 * any family any time. A family is the account the children hang off (the family account) plus,
 * optionally, a second adult (`family_adults`).
 *
 * Phone numbers here are full: the owner typed them or the member gave them to the business.
 */

/** Where a family stands with the invite: no message yet, invited and not in yet, or in the app. */
export const familyInviteStateSchema = z.enum(["none", "invited", "joined"]);
export type FamilyInviteState = z.infer<typeof familyInviteStateSchema>;

export const familyAdultSchema = z.object({
  userId: uuidSchema,
  name: z.string(),
  /** Null for a parent the owner listed with no number yet. */
  phoneE164: z.string().nullable(),
  /** True for the family account. The other adult is the second adult. */
  primary: z.boolean(),
  /** In the app: the number is proved and they joined the business. */
  joined: z.boolean(),
  invitedAt: isoDateTimeSchema.nullable(),
  /** The owner can change the name and the number only until the person signs in. */
  editable: z.boolean(),
  /**
   * The parent's profile photo (migration 0030; from WhatsApp sync, or saved by the owner). Signed,
   * expires in 12 to 24 hours; never store it. Optional for older API builds.
   */
  photoUrl: z.string().nullable().optional(),
});
export type FamilyAdult = z.infer<typeof familyAdultSchema>;

export const familyChildSchema = z.object({
  participantId: uuidSchema,
  name: z.string(),
  age: z.number().int().nullable(),
  groupId: uuidSchema.nullable(),
  placementStatus: placementStatusSchema,
  /** A child added from a list before the owner typed the name ("Child of Huda"). */
  nameMissing: z.boolean(),
  createdAt: isoDateTimeSchema,
  /** The profile photo (signed, expires in 12 to 24 hours; never store it). Optional for older API builds. */
  photoUrl: z.string().nullable().optional(),
  /** The participant kind. Optional for older fixtures and API builds. */
  kind: z.enum(["self", "child"]).optional(),
  /**
   * False when this business does not hold the row: the person's own row that belongs to another
   * business or to none (D36). The business does not change its name, age, group or note, and does
   * not remove it here (D26). Optional for older API builds (then true).
   */
  heldHere: z.boolean().optional(),
});
export type FamilyChild = z.infer<typeof familyChildSchema>;

export const familySchema = z.object({
  /** The family account. Every family route is addressed by it. */
  id: uuidSchema,
  adults: z.array(familyAdultSchema),
  children: z.array(familyChildSchema),
  invite: familyInviteStateSchema,
  membershipStatus: membershipStatusSchema.nullable(),
  holdNote: z.string().nullable(),
  createdAt: isoDateTimeSchema,
});
export type Family = z.infer<typeof familySchema>;

/** "Welcome back. Here is where you left off." The owner's last sitting before today. */
export const lastSittingSchema = z.object({
  at: isoDateTimeSchema,
  familiesAdded: z.number().int(),
  placed: z.number().int(),
});
export type LastSitting = z.infer<typeof lastSittingSchema>;

/** A person on the team, with the number the owner messages them on. */
export const familyStaffSchema = z.object({
  userId: uuidSchema,
  name: z.string(),
  phoneE164: z.string().nullable(),
  /** The profile photo (signed, expires in 12 to 24 hours; never store it). Optional for older API builds. */
  photoUrl: z.string().nullable().optional(),
});
export type FamilyStaff = z.infer<typeof familyStaffSchema>;

/** A group as the Families page needs it: who runs it and when it meets each week. */
export const familyGroupSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  color: z.string().nullable(),
  /** Seats per session, the most any of its weekly slots holds. */
  capacity: z.number().int().nullable(),
  staff: familyStaffSchema.nullable(),
  slots: z.array(z.object({ weekday: z.number().int().min(0).max(6), startTime: z.string() })),
});
export type FamilyGroup = z.infer<typeof familyGroupSchema>;

export const familiesResponseSchema = z.object({
  families: z.array(familySchema),
  groups: z.array(familyGroupSchema),
  lastSitting: lastSittingSchema.nullable(),
});
export type FamiliesResponse = z.infer<typeof familiesResponseSchema>;

const personNameSchema = z.string().trim().max(120);
const phoneInputSchema = z.string().trim().max(40);
const ageSchema = z.number().int().min(0).max(99);

export const newFamilyAdultSchema = z.object({
  name: personNameSchema,
  phone: phoneInputSchema.optional(),
});
export const newFamilyChildSchema = z.object({
  name: personNameSchema.min(1),
  age: ageSchema.nullable().optional(),
});

/**
 * `POST /business/businesses/:id/families`: one list of families. A family whose number is already
 * at the business is not added again. `groupId` puts every child of the list in one group;
 * `placeholderChildren` gives a family with no child a "Child of ..." row so it can sit in that
 * group until the owner types the name.
 */
export const addFamiliesRequestSchema = z.object({
  source: importSourceSchema,
  groupId: uuidSchema.nullable().optional(),
  unsure: z.boolean().optional(),
  placeholderChildren: z.boolean().optional(),
  families: z
    .array(
      z.object({
        adults: z.array(newFamilyAdultSchema).min(1).max(2),
        children: z.array(newFamilyChildSchema).max(12).default([]),
      }),
    )
    .min(1)
    .max(1000),
});
export type AddFamiliesRequest = z.input<typeof addFamiliesRequestSchema>;

/**
 * What one list added. Undo sends back only `id`: the server keeps the ids the list made, so a
 * client can never name other rows to delete (internal review F4.1).
 */
export const familiesBatchSchema = z.object({
  id: uuidSchema,
  familyIds: z.array(uuidSchema),
});
export type FamiliesBatch = z.infer<typeof familiesBatchSchema>;

export const undoFamiliesRequestSchema = z.object({ batchId: uuidSchema });
export type UndoFamiliesRequest = z.infer<typeof undoFamiliesRequestSchema>;

export const addFamiliesResponseSchema = z.object({
  added: z.number().int(),
  children: z.number().int(),
  /** Families with a number that is already at the business. */
  alreadyHere: z.number().int(),
  /** Numbers we could not read. Those adults were added with no number. */
  unreadable: z.number().int(),
  batch: familiesBatchSchema,
});
export type AddFamiliesResponse = z.infer<typeof addFamiliesResponseSchema>;

export const updateFamilyAdultRequestSchema = z.object({
  name: personNameSchema.min(1).optional(),
  /** An empty string removes the number. */
  phone: phoneInputSchema.optional(),
});
export type UpdateFamilyAdultRequest = z.infer<typeof updateFamilyAdultRequestSchema>;

export const addFamilyAdultRequestSchema = newFamilyAdultSchema.extend({ name: personNameSchema.min(1) });
export type AddFamilyAdultRequest = z.infer<typeof addFamilyAdultRequestSchema>;

export const addFamilyChildRequestSchema = newFamilyChildSchema.extend({
  groupId: uuidSchema.nullable().optional(),
});
export type AddFamilyChildRequest = z.infer<typeof addFamilyChildRequestSchema>;

export const familyHoldRequestSchema = z.object({ note: z.string().trim().min(1).max(300) });
export type FamilyHoldRequest = z.infer<typeof familyHoldRequestSchema>;

/** What "Remove family" changed, so Undo can put it back exactly. */
export const removedFamilySchema = z.object({
  familyId: uuidSchema,
  participantIds: z.array(uuidSchema),
  contactIds: z.array(uuidSchema),
  membershipId: uuidSchema.nullable(),
  membershipStatus: membershipStatusSchema.nullable(),
});
export type RemovedFamily = z.infer<typeof removedFamilySchema>;

/**
 * "Link a parent who is already here" (#ai-feedback 2026-10-26, card #14). One family of the check:
 * what moves when it goes into the other family.
 */
export const familyLinkSideSchema = z.object({
  familyId: uuidSchema,
  adults: z.array(z.string()),
  children: z.array(z.object({ participantId: uuidSchema, name: z.string() })),
  /** Packages with sessions left, and running memberships. */
  packages: z.number().int().nonnegative(),
  sessionsLeft: z.number().int().nonnegative(),
  upcomingBookings: z.number().int().nonnegative(),
  /** Why this family cannot stay while the other one moves into it. Null: it can. */
  keepRefusal: z.string().nullable(),
});
export type FamilyLinkSide = z.infer<typeof familyLinkSideSchema>;

/** `POST /business/businesses/:id/families/links/check`: the check screen before a link. Changes nothing. */
export const familyLinkCheckRequestSchema = z.object({ familyIds: z.tuple([uuidSchema, uuidSchema]) });
export type FamilyLinkCheckRequest = z.infer<typeof familyLinkCheckRequestSchema>;

export const familyLinkCheckSchema = z.object({
  sides: z.tuple([familyLinkSideSchema, familyLinkSideSchema]),
  /** Names of children in both families (a duplicate from the WhatsApp sync, for example). Teamna does not join them. */
  sameNameChildren: z.array(z.string()),
});
export type FamilyLinkCheck = z.infer<typeof familyLinkCheckSchema>;

/** `POST /business/businesses/:id/families/links`: the family `mergeFamilyId` goes into `keepFamilyId`. */
export const linkFamiliesRequestSchema = z.object({ keepFamilyId: uuidSchema, mergeFamilyId: uuidSchema });
export type LinkFamiliesRequest = z.infer<typeof linkFamiliesRequestSchema>;

export const linkFamiliesResponseSchema = z.object({
  /** The id that Undo sends back. */
  linkId: uuidSchema,
  family: familySchema,
});
export type LinkFamiliesResponse = z.infer<typeof linkFamiliesResponseSchema>;

/** `POST /business/businesses/:id/families/invite`: an invite to every adult of these families who is not in yet. */
export const inviteFamiliesRequestSchema = z.object({
  familyIds: z.array(uuidSchema).min(1).max(1000),
  /** Only adults with no invite yet: an adult invited before (another tab, the Parents page) gets nothing. */
  onlyNew: z.boolean().optional(),
});
export type InviteFamiliesRequest = z.infer<typeof inviteFamiliesRequestSchema>;

export const inviteFamiliesResponseSchema = z.object({
  /** Adults the invite went to. */
  sent: z.number().int(),
  families: z.number().int(),
  /** True while WhatsApp is not connected: the invite is recorded, not sent. */
  simulated: z.boolean(),
});
export type InviteFamiliesResponse = z.infer<typeof inviteFamiliesResponseSchema>;

export const inviteLogEntrySchema = z.object({
  id: uuidSchema,
  label: z.string(),
  sentAt: isoDateTimeSchema,
  recipients: z.number().int(),
});
export const inviteLogSchema = z.object({
  code: z.string(),
  url: z.string(),
  businessName: z.string(),
  simulated: z.boolean(),
  sends: z.array(inviteLogEntrySchema),
});
export type InviteLog = z.infer<typeof inviteLogSchema>;
